import React from 'react';
import { Typography, theme } from 'antd';

const { Title, Text } = Typography;
const { useToken } = theme;

// Header tipo "toolbar" para la parte superior de una Card o panel bordeado.
// Pensado para dashboards donde cada panel repite el mismo encabezado
// (título pequeño uppercase + subtítulo + acción opcional a la derecha).
//
// Props:
// - title: string | ReactNode (requerido)
// - subtitle: string | ReactNode
// - icon: componente de @ant-design/icons (opcional, inline con el título)
// - tint: color del icono (default: colorPrimary)
// - action: ReactNode alineado a la derecha (link, botón, filtro…)
// - size: 'sm' | 'md' — altura y padding (default 'md')
// - bordered: border-bottom (default true)
// - filled: bg sutil tipo barra (default true)
//
// Uso:
//   <div style={{ border: `1px solid ${token.colorBorder}`, borderRadius: token.borderRadiusLG, overflow: 'hidden' }}>
//     <CardHeader title="Carga actual por técnico" subtitle="..." action={<Link>Ver</Link>} />
//     <div style={{ padding: 16 }}>...body...</div>
//   </div>

const SIZES = {
  sm: { padY: 8, padX: 14, titleSize: 12, subtitleSize: 11 },
  md: { padY: 12, padX: 20, titleSize: 13, subtitleSize: 11 },
};

function CardHeader({
  title,
  subtitle,
  icon: Icon,
  tint,
  action,
  size = 'md',
  bordered = true,
  filled = true,
}) {
  const { token } = useToken();
  const resolvedTint = tint ?? token.colorPrimary;
  const { padY, padX, titleSize, subtitleSize } = SIZES[size] ?? SIZES.md;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: `${padY}px ${padX}px`,
        background: filled ? token.colorFillAlter : 'transparent',
        borderBottom: bordered
          ? `1px solid ${token.colorBorderSecondary}`
          : 'none',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            minWidth: 0,
          }}
        >
          {Icon && (
            <Icon
              aria-hidden="true"
              style={{ color: resolvedTint, fontSize: titleSize + 1 }}
            />
          )}
          <Title
            level={5}
            style={{
              margin: 0,
              fontSize: titleSize,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              color: token.colorTextSecondary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {title}
          </Title>
        </div>
        {subtitle && (
          <Text
            style={{
              display: 'block',
              fontSize: subtitleSize,
              color: token.colorTextTertiary,
              marginTop: 2,
              lineHeight: 1.4,
            }}
          >
            {subtitle}
          </Text>
        )}
      </div>
      {action && <div style={{ flexShrink: 0 }}>{action}</div>}
    </div>
  );
}

export default CardHeader;
